/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";

type LegalExpenseInsuranceProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function LegalExpensesInsurance({ submitProps, product }: LegalExpenseInsuranceProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Persönliche Angaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"isCostCovered"}
                question={"Übernimmt bereits jetzt schon Ihre Rechtsschutzversicherung oder eine andere Stelle oder Person die Kosten für das Verfahren?"}
                modalContent={
                  <p>
                    Gemeint ist hier, ob bereits eine Entscheidung zur Übernahme der Kosten vorliegt. Bei einer Scheidung ist die Übernahme durch eine RSV nur
                    sehr selten. <strong>Wichtig zu wissen:</strong> Wenn eine Rechtsschutzversicherung oder eine andere Organisation die Verfahrenskosten in
                    voller Höhe trägt, kommt eine Bewilligung von Prozess- oder Verfahrenskostenhilfe nicht in Betracht und damit ist die Beantwortung der
                    weiteren Fragen nicht erforderlich.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.isCostCovered === "yes" && (
              <NumberFieldConfigured caseData={product} fieldPath={"costCoverageAmount"} required fieldType={"money"} numberType={"float"} />
            )}
            {values.isCostCovered === "no" && (
              <>
                <Grid size={12}>
                  <Group
                    type={"radio"}
                    name={"hasPotentialCoverage"}
                    question={
                      "Haben Sie eine Rechtsschutzversicherung oder sind Sie Mitglied in einem Verein oder einer Organisation, die die Kosten übernehmen könnte?"
                    }
                    modalContent={<p>Beispiele hierfür sind eine Gewerkschaft, ein Mieterverein oder ein Sozialverband.</p>}
                    options={[
                      {
                        labelText: "Nein",
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                      {
                        labelText: "Ja",
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                {values.hasPotentialCoverage === "yes" && (
                  <Grid size={12}>
                    <ValidatorTextField
                      name="potentialCoverer"
                      label="Name Versicherung/Verein"
                      validators={[requiredValidator("Bitte geben Sie die Versicherung/ den Verein an")]}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
